import { useState, useEffect } from 'react'
import AlphaGate from './AlphaGate'
import Landing from './Landing'
import { supabase } from './supabase'
import Layout from './Layout'
import Signup from './Signup'
import Login from './Login'
import Home from './Home'
import GolfProfile from './GolfProfile'
import GamePicker from './GamePicker'
import CreateMatch from './CreateMatch'
import InvitePlayers from './InvitePlayers'
import MatchSchedule from './MatchSchedule'
import MatchLobby from './MatchLobby'
import Profile from './Profile'
import Wallet from './Wallet'
import Scorecard from './Scorecard'
import Invites from './Invites'
import CrossbetPicker from './CrossbetPicker'
import OneVOne from './OneVOne'
import BettingLedger from './BettingLedger'
import Friends from './Friends'
import CreateTable from './CreateTable'
import TableLobby from './TableLobby'

const NO_LAYOUT = ['signup', 'login']

const BACK_MAP = {
  match_schedule: 'home',
  match_lobby: 'match_schedule',
  invite_players: 'match_lobby',
  create_match: 'crossbet_picker',
  crossbet_picker: 'home',
  one_v_one: 'crossbet_picker',
  betting_ledger: 'home',
  friends: 'home',
  create_table: 'crossbet_picker',
  table_lobby: 'crossbet_picker',
  sport_picker: 'crossbet_picker',
  profile: 'home',
  golf_profile: 'home',
  wallet: 'home',
  scorecard: 'match_lobby',
  invites: 'home',
}

export default function App() {
  const [alphaUnlocked, setAlphaUnlocked] = useState(localStorage.getItem('crossbet_alpha') === '1')
  const [session, setSession] = useState(null)
  const [ready, setReady] = useState(false)
  const [screen, setScreen] = useState(() => sessionStorage.getItem('screen') || 'home')
  const [matchId, setMatchId] = useState(() => sessionStorage.getItem('matchId') || null)
  const [editingMatch, setEditingMatch] = useState(null)

  const goTo = (s) => { setScreen(s); sessionStorage.setItem('screen', s) }
  const saveMatch = (id) => { setMatchId(id); id ? sessionStorage.setItem('matchId', id) : sessionStorage.removeItem('matchId') }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setReady(true)
    })
    supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session)
    })
  }, [])

  useEffect(() => {
    if (!session) return
    const autoSettle = async () => {
      const now = new Date()
      const { data: lines } = await supabase.from('betting_lines').select('*').eq('status', 'active').filter('player_ids', 'cs', `{"${session.user.id}"}`)
      if (!lines || lines.length === 0) return
      for (const line of lines) {
        const { data: scs } = await supabase.from('scorecards').select('*').eq('match_id', line.match_id).in('player_id', line.player_ids)
        if (!scs || scs.length === 0) continue
        const expired = scs.some(sc => sc.review_started_at && (now - new Date(sc.review_started_at)) >= 24 * 60 * 60 * 1000)
        if (!expired) continue
        for (const sc of scs) {
          if (sc.status === 'review' && sc.review_started_at && (now - new Date(sc.review_started_at)) >= 24 * 60 * 60 * 1000) {
            await supabase.from('scorecards').update({ status: 'closed', closed_at: now.toISOString(), agreed_by: [...new Set([...(sc.agreed_by || []), sc.player_id])] }).eq('id', sc.id)
          }
        }
        await supabase.from('betting_lines').update({ status: 'settled', settled_by: line.player_ids }).eq('id', line.id)
      }
    }
    autoSettle()
  }, [session])

  if (!alphaUnlocked) return <AlphaGate onUnlock={() => setAlphaUnlocked(true)} />
  if (!session) {
    if (screen === 'login') return <Login onSuccess={() => goTo('home')} onSignup={() => goTo('signup')} />
    if (screen === 'signup') return <Signup onSuccess={() => goTo('home')} onLogin={() => goTo('login')} />
    return <Landing onSignup={() => goTo('signup')} onLogin={() => goTo('login')} />
  }

  if (!ready) return (
    <div style={{ minHeight: '100vh', background: '#FDF9E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ color: '#7A7570' }}>Loading...</div>
    </div>
  )

  const handleNavigate = (id) => {
    if (id === 'create_match') { setEditingMatch(null); goTo('crossbet_picker') }
    else goTo(id)
  }

  const handleBack = () => {
    if (screen === 'create_match' && editingMatch) { setEditingMatch(null); goTo('match_schedule'); return }
    goTo(BACK_MAP[screen] || 'home')
  }

  const signOut = () => supabase.auth.signOut().then(() => { sessionStorage.clear() })



  const renderScreen = () => {
    if (screen === 'signup') return <Signup onSuccess={() => goTo('home')} onLogin={() => goTo('login')} />
    if (screen === 'login') return <Login onSuccess={() => goTo('home')} onSignup={() => goTo('signup')} />
    if (screen === 'golf_profile') return <GolfProfile session={session} onComplete={() => goTo('home')} onSkip={() => goTo('home')} />

    if (screen === 'profile') return <Profile session={session} onBack={handleBack} onSignOut={signOut} />
    if (screen === 'invites') return <Invites session={session} onOpenLobby={(id) => { saveMatch(id); goTo('match_lobby') }} />
    if (screen === 'wallet') return <Wallet />
    if (screen === 'crossbet_picker') return <CrossbetPicker onSelectMatch={() => goTo('sport_picker')} onSelect1v1={() => goTo('one_v_one')} onSelectTable={() => goTo('create_table')} onSelectSeasonals={() => {}} />
    if (screen === 'one_v_one') return <OneVOne session={session} onBack={handleBack} />
    if (screen === 'betting_ledger') return <BettingLedger session={session} />
    if (screen === 'friends') return <Friends session={session} />
    if (screen === 'create_table') return <CreateTable session={session} onBack={handleBack} onCreated={(id) => { sessionStorage.setItem('tableId', id); goTo('table_lobby') }} />
    if (screen === 'table_lobby') return <TableLobby session={session} tableId={sessionStorage.getItem('tableId')} onBack={handleBack} onOpenTable={(id) => { sessionStorage.setItem('tableId', id); goTo('table_lobby') }} />
    if (screen === 'sport_picker') return <GamePicker session={session} onBack={handleBack} onSelect={() => goTo('create_match')} />
    if (screen === 'create_match') return <CreateMatch session={session} editingMatch={editingMatch} onBack={handleBack} onMatchCreated={(id) => { saveMatch(id); if (editingMatch) { setEditingMatch(null); goTo('match_schedule') } else { goTo('invite_players') } }} />
    if (screen === 'invite_players') return <InvitePlayers session={session} matchId={matchId} onBack={handleBack} onDone={() => goTo('match_lobby')} />
    if (screen === 'match_lobby') return <MatchLobby session={session} matchId={matchId} onBack={handleBack} onOpenScorecard={() => goTo('scorecard')} onInviteMore={() => goTo('invite_players')} />
    if (screen === 'scorecard') return <Scorecard session={session} matchId={matchId} onBack={handleBack} />
    if (screen === 'match_schedule') return <MatchSchedule session={session} onBack={handleBack} onCreateMatch={() => { setEditingMatch(null); goTo('crossbet_picker') }} onEditMatch={(m) => { setEditingMatch(m); goTo('create_match') }} onOpenLobby={(id) => { saveMatch(id); goTo('match_lobby') }} onOpenTable={(id) => { sessionStorage.setItem('tableId', id); goTo('table_lobby') }} />
    if (screen === 'home') return <Home session={session} onSignOut={signOut} onCreateMatch={() => { setEditingMatch(null); goTo('crossbet_picker') }} onSchedule={() => goTo('match_schedule')} onProfile={() => goTo('profile')} onOpenLobby={(id) => { saveMatch(id); goTo('match_lobby') }} onOpenTable={(id) => { sessionStorage.setItem('tableId', id); goTo('table_lobby') }} />
    return <Home session={session} onSignOut={signOut} onCreateMatch={() => goTo('crossbet_picker')} onSchedule={() => goTo('match_schedule')} onProfile={() => goTo('profile')} onOpenLobby={(id) => { saveMatch(id); goTo('match_lobby') }} onOpenTable={(id) => { sessionStorage.setItem('tableId', id); goTo('table_lobby') }} />
  }

  if (NO_LAYOUT.includes(screen)) return renderScreen()

  return (
    <Layout screen={screen} onNavigate={handleNavigate} onBack={handleBack} showBack={screen !== 'home'} session={session}>
      {session && renderScreen()}
    </Layout>
  )
}
